import { CORPUS_COVERAGE_GROWS_CLAUSE } from '../lib/whitespace'

// Centralised help copy (Spec §6.3) — "the heart of the redesign".
// One map keyed by screen/section id, consumed by HelpPopover (inline ?),
// the ResourcesPanel (searchable articles), empty states, and tours. Keeping
// it here makes help consistent, searchable, and translatable, and keeps the
// specialised concepts (invention declaration, the disclosure ladder & L3→L4
// NDA gate, blind matching, BYOK custody, FGAC role mapping) explained at the
// point of use instead of nowhere.

export interface HelpArticle {
  /** stable key — matches NavLink.helpKey or a section id */
  key: string
  title: string
  /** short one/two-sentence body shown in the ? popover */
  body: string
  /** optional deeper paragraphs shown in the Resources panel */
  detail?: string[]
  /** search/browse categorisation */
  category:
    | 'Getting started'
    | 'Innovations'
    | 'Research'
    | 'Network'
    | 'Organisation'
    | 'Aggregator'
    | 'White space'
    | 'Account'
    | 'Concepts'
}

// design §5.8: "The honesty sentences ... live ONCE in the registry so every
// surface quotes the same words" — this is the map's own honesty caveat,
// quoted verbatim by both the ws-map article below and the project-side
// "Reading your white-space scan" tour's map step, so the two can never
// drift into two different wordings of the same claim.
export const MAP_PROJECTION_HONESTY_NOTE =
  'Positions on this map are a reading of a flattened 2-D projection, not a measurement — always check a candidate direction’s real score rather than treating a map position alone as proof.'

export const HELP_ARTICLES: HelpArticle[] = [
  {
    key: 'home',
    title: 'Your Home dashboard',
    body: 'A single place to see what needs your attention and start your next action. New here? Begin with “Create your first disclosure”.',
    category: 'Getting started',
  },
  {
    key: 'projects',
    title: 'Projects',
    body: 'Each project groups the documents, analysis, matches and contacts for one innovation. Open a project to move it through the pipeline.',
    category: 'Innovations',
  },
  {
    key: 'new-disclosure',
    title: 'New Disclosure',
    body: 'The guided way to submit an invention: add files, describe it, then review and submit. The system runs intake and AI analysis for you.',
    detail: [
      'A disclosure is your formal, timestamped record that you invented something. Submitting it starts the innovation pipeline: Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure.',
    ],
    category: 'Innovations',
  },
  {
    key: 'upload',
    title: 'Add documents',
    body: 'Attach files to an existing project. Supported documents are classified automatically; you can override the type if we get it wrong.',
    category: 'Innovations',
  },
  {
    key: 'patents',
    title: 'Patent search',
    body: 'Search worldwide patents for prior art. Save results to a list, or attach them to a project as references.',
    category: 'Research',
  },
  {
    key: 'journals',
    title: 'Journal search',
    body: 'Search open-access research. Save papers to a list or link them to a project to build your evidence base.',
    category: 'Research',
  },
  {
    key: 'search',
    title: 'Global search',
    body: 'Search across your projects, documents, patents and journals from one place.',
    category: 'Research',
  },
  {
    key: 'lists',
    title: 'Saved Lists',
    body: 'Your personal collections of patents, journals and discoveries. Attach a list item to a project when it becomes relevant.',
    category: 'Research',
  },
  {
    key: 'ecosystem',
    title: 'Ecosystem',
    body: 'Discover organisations and experts you could collaborate with, then connect and link them to a project.',
    category: 'Network',
  },
  {
    key: 'crm',
    title: 'Contacts & CRM',
    body: 'Track the people and organisations in your innovation network, and the tasks that move relationships forward.',
    category: 'Network',
  },
  {
    key: 'tasks',
    title: 'Tasks & Reviews',
    body: 'Everything waiting on you — review requests, approvals and to-dos — in one queue.',
    category: 'Getting started',
  },
  {
    key: 'shared',
    title: 'Shared with me',
    body: 'Projects and documents others have shared with you. What you can see and do depends on the disclosure level you were granted.',
    category: 'Network',
  },
  {
    key: 'org-members',
    title: 'Members & Licences',
    body: 'Invite people to your organisation and assign the licences that grant them access.',
    category: 'Organisation',
  },
  {
    key: 'org-access',
    title: 'Project & Folder Access',
    body: 'Grant or revoke access to specific projects and folders for members of your organisation.',
    category: 'Organisation',
  },
  {
    key: 'org-identity',
    title: 'Identity & SSO',
    body: 'Connect your identity provider so members sign in with your organisation’s single sign-on.',
    category: 'Organisation',
  },
  {
    key: 'org-keys',
    title: 'Organisation Encryption Keys',
    body: 'Choose platform-managed encryption or bring your own key (BYOK). Your documents are always encrypted at rest.',
    category: 'Organisation',
  },
  {
    key: 'agg-portfolio',
    title: 'Portfolio',
    body: 'Every member entity’s projects in one place — triage inbound submissions, group, filter and run bulk actions. The “N selected” count always reflects the current filter.',
    detail: [
      'The Atlas, Synergy Matrix, Overlap, Topology & Strategy and Watch tabs above the grid give you portfolio-wide readings of the same entitled projects — each has its own ? for how to read it.',
    ],
    category: 'Aggregator',
  },
  {
    key: 'agg-team',
    title: 'Team',
    body: 'Manage the analysts and billing owners who work across your portfolio.',
    category: 'Aggregator',
  },
  {
    key: 'agg-entity-group',
    title: 'Entity',
    body: 'An entity is one inventor — or one company\u2019s inventors — as they chose to share it: granted projects, minus any folders they hid. What you see of it is exactly what its owners chose to share with your portfolio, and nothing more.',
    category: 'Aggregator',
  },
  {
    key: 'agg-across-portfolios',
    title: 'Across portfolios',
    body: 'The edges of your portfolio: every disclosure agreement an entity you can see is party to, at its current rung, plus what other organisations shared in. Identities appear only from rung 4, after a mutual NDA. There is no cross-portfolio matching \u2014 nothing here comes from another organisation\u2019s portfolio.',
    category: 'Aggregator',
  },
  {
    key: 'agg-inbound',
    title: 'Inbound',
    body: 'Scored, blind-matched disclosures arriving for your portfolio. Scores are real matching results — never placeholders. Triage the top matches, then invite or accept.',
    category: 'Aggregator',
  },
  {
    key: 'agg-billing',
    title: 'Aggregator billing',
    body: 'Payment details and invoices for this portfolio. Nothing here changes what anyone can see: what a portfolio sees is decided by the role a person is acting as and by what inventors have shared with it.',
    category: 'Aggregator',
  },

  // ── White space (project) — Story #5305 Task #5327 Stream 11c ──
  {
    key: 'ws-overview',
    title: 'White Space Discovery',
    body: 'A scan places your project against the patent and journal corpus and scores every distance ring around it — from Very close out to Far afield — so you can see where the ground is crowded and where it is genuinely open.',
    detail: [
      `Scores are always relative to the loaded corpus, not the full universe of prior art — coverage grows ${CORPUS_COVERAGE_GROWS_CLAUSE}, and the screen says so every time.`,
      'Four views read the same scan differently: the Opportunity Explorer ranks scored regions, the Combination Matrix crosses your idea’s features against application domains, the Landscape Map plots everything in 2-D, and the 3-D Explorer adds a third dimension.',
    ],
    category: 'White space',
  },
  {
    key: 'ws-how-scored',
    title: 'How the White-Space score is built',
    // design §5.1 "one metric gets one name" — this article names all four
    // sub-scores, so it must use the exact same plain-English names the
    // card and its "Why this score?" breakdown use (see the ws-sub-* articles
    // just above), never the raw internal metric names they replaced.
    body: 'Each region’s 0–100 score combines four measured sub-scores — Openness, Nearby activity, Filing momentum and Feasibility signal — weighted 35/25/20/20. Open “Why this score?” under any card to see each sub-score’s real count.',
    detail: [
      'Openness alone can’t win top spot: a region can score high on emptiness for the boring reason that nobody CAN build there. When nearby activity, filing momentum and feasibility signal are all low, openness’s usable contribution is capped — otherwise infeasible dead zones would top the list.',
      `The score is a reading of the loaded corpus, not a measurement of the real world’s prior art — it moves ${CORPUS_COVERAGE_GROWS_CLAUSE}.`,
    ],
    category: 'White space',
  },
  {
    key: 'ws-bands',
    title: 'Distance bands — the ladder every screen shares',
    body: 'Very close, Close, Moderately close, Further out, Far afield — five rings of similarity to your project, tightest to widest. It is the one word for this idea everywhere in the app: the explorer cards, the map and the matrix all use it.',
    detail: [
      'A band names a DISTANCE, never a place — two records in the same band can sit on opposite sides of your idea. That is exactly why band names are distance words like “Close”, never place words.',
      'A run of adjacent bands with nothing recorded in any of them collapses into one card, so an empty stretch of the ladder does not take up five cards’ worth of space.',
    ],
    category: 'White space',
  },
  {
    key: 'ws-why-ranked',
    title: 'Why cards are ranked the way they are',
    body: 'Ranked by opportunity, not emptiness — a region with supporting literature but no filings outranks a totally silent one.',
    detail: [
      'Ranking is opportunity-first, not distance-first: the closest ring to your project is not always the top card if a further-out ring has a stronger score.',
      'A silent region (nothing recorded anywhere) still gets a full, honest reading — see “How the score is built” for why a merely-empty region does not automatically outrank one backed by real published research.',
    ],
    category: 'White space',
  },
  {
    // design §5.1 "one metric gets one name" — this article's title (shown
    // as the help popover's own header, HelpPopover.tsx's `resolvedTitle`)
    // must match the card's plain-English sub-score label exactly, never
    // the raw internal metric name it replaced.
    key: 'ws-sub-sparsity',
    title: 'Openness',
    body: 'How little has actually been filed or published at this exact distance from your project — the number of nearby records is the real count behind it. The single biggest input, weighted 35 of the composite score.',
    category: 'White space',
  },
  {
    key: 'ws-sub-adjacency',
    title: 'Nearby activity',
    body: 'Whether there is ENOUGH related patent activity nearby to make a region buildable, without so much that it is already staked out. This score is band-passed, not “more is always better”.',
    detail: [
      'The sweet spot sits roughly between 20 and 50 on the 0–100 scale — enough enabling technology nearby, not so much the ground is already occupied. A totally isolated region with near-zero nearby activity often is not actually buildable.',
    ],
    category: 'White space',
  },
  {
    key: 'ws-sub-momentum',
    title: 'Filing momentum',
    body: 'How recently patents have been filed in this band — a real recency signal computed from filing dates, not a raw count. Rising filing momentum in an otherwise open band is an early warning that others are starting to move into it.',
    category: 'White space',
  },
  {
    key: 'ws-sub-enablement',
    title: 'Feasibility signal',
    body: 'How much of this band’s published research your project’s own documents already cover — real papers, not an invented checklist. A high feasibility signal means the science is published even where nobody has filed a patent yet.',
    category: 'White space',
  },
  {
    key: 'ws-map',
    title: 'The Landscape Map',
    body: 'A 2-D picture of your idea against the corpus: the amber star is you, small dots are nearby patents and papers, and dashed amber circles mark the same scored regions as the Opportunity Explorer — a visual, not a new calculation.',
    detail: [
      'Corpus density shows as a soft blue haze, binned rather than one dot per record, so a crowded area reads as crowded without drawing thousands of points.',
      'Hover a dashed region and a small purple “P” marker lets you probe that exact spot — see “Probing a direction” for what that does.',
      MAP_PROJECTION_HONESTY_NOTE,
    ],
    category: 'White space',
  },
  {
    key: 'ws-probes',
    title: 'Probing a direction',
    body: 'Describe a variation of your idea in a sentence, or ask for AI-suggested directions — either way it is embedded with the same model used everywhere else and placed on the map so you can compare it against the ranked regions.',
    detail: [
      'A probe’s distance to the nearest existing point uses its own honest ruler (projected-space units, lower = closer) — never the 0–100 similarity scale scored regions use, because a map position and a similarity score measure different things.',
      'Accepting a probe hands it straight to a real novelty analysis run and returns a receipt so you can find it again.',
      'A plausibility flag, when shown, is a real signal computed from the probe itself, not a fixed disclaimer.',
    ],
    category: 'White space',
  },
  {
    key: 'ws-novelty-handoff',
    title: 'Handing off to a novelty probe',
    body: '“Run novelty probe” takes a scored region — or an accepted map probe — straight into this project’s existing AI Analysis tab: the same full novelty-analysis pipeline every other entry point in the app uses, not a separate white-space-only feature.',
    detail: ['White Space itself never runs the novelty analysis — it only carries the region or probe text across as a starting point.'],
    category: 'White space',
  },
  {
    key: 'ws-matrix',
    title: 'The Combination Matrix',
    body: 'Crosses your idea’s extracted features against real application domains drawn from your last scan’s own records — each cell is the share of a feature’s matched neighbourhood that falls in that domain, not a raw count against the whole corpus.',
    detail: [
      'Dashed cells are combination white space: both the feature and the domain exist elsewhere, but the pairing between them does not — genuinely unexplored, not just under-filed.',
      'The density legend is re-derived from this matrix’s own real share distribution every time, never a fixed corpus-sized scale, so “crowded” always means crowded for YOUR scan.',
      'Needs your idea broken into extracted features first — a project with none yet is offered a consent-gated “Extract features” action that spends AI credits.',
    ],
    category: 'White space',
  },
  {
    key: 'ws-3d',
    title: 'The 3-D Explorer',
    body: 'The same scored landscape as the 2-D map with a third dimension added — a density fog of the surrounding corpus with your own project’s documents marked distinctly, built once per visit and cached until you ask for a fresh build.',
    detail: [
      'The X/Y/Z/colour/size dials choose what each axis and encoding actually shows — every assigned channel names its attribute and real value range, so nothing is an unlabelled scale.',
      'Double-click a dense cluster to drill in to that cluster’s own individual records — the only place this view renders individual records rather than a binned density fog.',
      'A first build can take up to about a minute; every later view of the same scan reuses the cached layout.',
    ],
    category: 'White space',
  },
  {
    key: 'ws-watch',
    title: 'Watching a region',
    body: 'Tracks one distance band on this project so it is automatically checked again every time you rescan — you do not have to remember which bands looked interesting last time.',
    detail: [
      'Watching a collapsed, all-empty card watches every band it covers in one action — all-or-nothing, never a partial state left behind if one of several watches fails to save.',
      'This is the project-level watch. An aggregator managing a portfolio of your organisation’s projects can additionally track the same kind of region across every project it can see (Portfolio Watch).',
    ],
    category: 'White space',
  },

  // ── White space (portfolio) — Story #5305 Stream 14d ──
  {
    key: 'pf-atlas',
    title: 'Portfolio Atlas',
    body: 'One joint map of every project you can see in this portfolio, plotted together so you can spot where they cluster, where they don’t, and where two projects are quietly covering the same ground.',
    detail: [
      'Each project gets its own colour and a starred centre point. A footprint line traces the boundary of what a project actually covers — two overlapping footprints are your first visual clue that two projects are working the same space, before you ever open the Overlap tab.',
      'Amber markers are the same holes, bridges and frontier directions a project-level White Space scan finds, rolled up across the portfolio and validated in the full similarity space before they’re drawn — never a two-dimensional projection artefact.',
      'Shared-in sources (projects another organisation has shared with you) carry no document-level position the disclosure ladder allows placing on the map, so they’re listed as chips beside the map instead of invented as points.',
    ],
    category: 'White space',
  },
  {
    key: 'pf-synergy',
    title: 'Synergy Matrix',
    body: 'Finds where two projects in your portfolio are complementary — combining them creates something neither has alone. This is the opposite question to Overlap, which finds projects doing the same work.',
    detail: [
      'Cells are real, specific matches between one project’s idea-component and another’s, not a prior-art density count. A cell you don’t see means no strong match was found for that pair — never a hidden zero.',
      'Synergy and Overlap can both fire on the same pair of projects. When they do, each view says so and links to the other rather than leaving you to reconcile two different verdicts.',
    ],
    category: 'White space',
  },
  {
    key: 'pf-overlap',
    title: 'Reading an Overlap group',
    body: 'An Overlap group is two or more projects your scan judged to be working the same ground — their documents sit in intersecting territory, not just nearby. Groups form only from real matched documents, computed over everything you’re entitled to see.',
    detail: [
      'The headline is a verdict, not just a number — “Substantially the same work” when both projects overlap heavily, “X sits inside Y — a consolidation candidate” when a smaller project sits entirely within a bigger one’s ground.',
      'The score under a group is its strongest pair, and it is a share, not a similarity — the fraction of the smaller project’s comparable documents that found a close match in the other. That is why it reads “Strongest pair: 93 — the share of the smaller project’s documents with a match in the other”, never “93% similar”.',
      'Weak signals (score 25–39) are shown only when you choose to look, and never form a group or send a notification — so a large portfolio is never flooded with borderline pairs.',
      'A group with very few documents on its smaller side is marked “worth a look” rather than given a strong verdict — treat those as a lead, not a conclusion.',
    ],
    category: 'White space',
  },
  {
    key: 'pf-overlap-actions',
    title: 'Acting on an overlap group',
    body: 'Every group carries a status you and your team move forward as you work it: New → Reviewing → Consolidating → Consolidated, or Dismissed if it turns out to be a false positive.',
    detail: [
      'Dismissing a group or marking it Consolidated needs an admin or the billing owner; any team member with access to every project in the group can set it to Reviewing and leave a note. Every change is recorded.',
      'A dismissed group stays dismissed even if its score later drifts into the weak-signal range — you never have to dismiss the same false positive twice.',
      '“Consolidated” records the outcome on the platform; the funding submission itself still happens outside it, and the record says so plainly.',
      'The fund-once workbench, below the group detail, drafts a consolidated funding case (.docx), searches the Directory for funding bodies, and — for an admin or the billing owner — raises the group with its project leads: a confirm step always shows the exact message and recipients before the one-shot send.',
      'A note you add here is encrypted under your organisation’s own key before it is stored. If no organisation key is registered yet, notes are simply unavailable — you’ll see plain copy saying so, and everything else on this page keeps working.',
    ],
    category: 'White space',
  },
  {
    key: 'inv-overlap-notify',
    title: 'Why you were notified',
    body: 'One of your organisation’s aggregator portfolios flagged your project as possibly working the same ground as another project it manages. The message you received names only your own project — never the other one.',
    detail: [
      'This is a lead, not a verdict: it means the two projects’ documents matched closely enough to be worth a look, not that they are definitely duplicates.',
      'Nothing about the other project — its name, its owner, or how many documents it has — is shared with you. If you want to compare notes, that is a conversation for the aggregator to start.',
      'You can keep working on your project as normal; this notification does not change anything about it on its own.',
    ],
    category: 'White space',
  },
  {
    key: 'pf-shape',
    title: 'Portfolio shape at a glance',
    body: 'A quick read of how your portfolio is holding together: how many gaps and connecting bridges its projects share, how many directions look worth exploring, and how much of the raw geometry was thrown out as a two-dimensional-only artefact.',
    detail: [
      'Holes and Bridges are the same validated findings a project-level White Space topology surfaces, rolled up across every project you can see — never re-derived from the flattened map alone.',
      '“2-D artifacts discarded” is an honesty count: candidate shapes the flattened map suggested that did NOT hold up once checked against the real, much higher-dimensional data — proof the numbers beside it were actually checked, not eyeballed off a picture.',
      'Open Topology & Strategy for the full breakdown and recommended next moves.',
    ],
    category: 'White space',
  },
  {
    key: 'pf-watch',
    title: 'Portfolio Watch',
    body: 'Track a white-space region across your whole portfolio, not just the one project it was found in — every rescan re-checks it against every member project you’re currently entitled to see.',
    detail: [
      'A watch is scoped to one region: one corpus, one distance band. If the region you watch turns out to really be several adjacent bands with nothing recorded, watching it can create more than one watch row here — each tracked and removable on its own, never silently merged into one.',
      'The pressure table below counts competitor filings landing inside your watched regions over a rolling window — a rising count is your earliest warning that ground you’re watching is getting crowded.',
      'Export a portfolio brief any time from here — a real .docx snapshot of your portfolio’s current watches and pressure, not a template.',
    ],
    category: 'White space',
  },

  {
    key: 'org-overview',
    title: 'Organisation overview',
    body: 'A guided starting point for administering your organisation: Seats, Access, SSO and Encryption Keys, each with its completion state and the next step to take.',
    category: 'Organisation',
  },
  {
    key: 'admin-overview',
    title: 'Platform Admin',
    body: 'Platform-wide health, users and audit — visible only to InnovateFlow administrators.',
    category: 'Account',
  },
  {
    key: 'admin-users',
    title: 'Platform Users',
    body: 'Every user on the platform. Search, inspect and manage accounts — InnovateFlow administrators only.',
    category: 'Account',
  },
  {
    key: 'admin-audit',
    title: 'Audit Log',
    body: 'The tamper-evident record of security-relevant actions across the platform.',
    category: 'Account',
  },
  {
    key: 'admin-orgs',
    title: 'Organisations',
    body: 'Every organisation on the platform — inspect, set the org type, and manage members. InnovateFlow administrators only.',
    category: 'Account',
  },
  {
    key: 'admin-security',
    title: 'Security reports',
    body: 'Permission changes, failed access attempts and security events across the platform.',
    category: 'Account',
  },
  {
    key: 'admin-settings',
    title: 'Platform settings',
    body: 'Platform-level configuration, distinct from your personal account settings. InnovateFlow administrators only.',
    category: 'Account',
  },
  {
    key: 'keypair-setup',
    title: 'Set up your keypair',
    body: 'A device keypair signs your disclosure commitments so they cannot be forged or denied. The private key never leaves your device. Required to advance disclosure to full sharing.',
    category: 'Concepts',
  },
  {
    key: 'account-settings',
    title: 'Account settings',
    body: 'Your profile, name and how you appear to collaborators.',
    category: 'Account',
  },
  {
    key: 'account-security',
    title: 'Security & Keys',
    body: 'Your personal encryption keypair and device setup — the prerequisite for receiving disclosures.',
    category: 'Account',
  },
  {
    key: 'account-billing',
    title: 'Billing',
    body: 'Your subscription tier, usage and invoices.',
    category: 'Account',
  },
  {
    key: 'account-preferences',
    title: 'Preferences',
    body: 'Personalise the interface — density, defaults and notifications.',
    category: 'Account',
  },
  {
    // Story #5648 build v2, screen C2. The answer this screen's ? has to give,
    // because it is the question the whole page exists for.
    key: 'account-sharing',
    title: 'Who can see what of mine',
    body: 'You decide what each organisation sees: grant a project, hide folders, set an expiry, revoke. Being part of a portfolio never shows them your work — sharing is yours to give.',
    category: 'Account',
  },

  // ── Concepts (glossary) — the specialised ideas the app assumes ──
  {
    key: 'concept-disclosure-ladder',
    title: 'The disclosure ladder',
    body: 'Sensitive detail is revealed one rung at a time across six levels. Both parties must consent to each step; rung 3→4 requires a signed NDA.',
    detail: [
      'Blind matching surfaces a potential match without revealing either party’s confidential detail. As trust is established, both sides climb the same six-rung ladder together: 1 Anonymous Match → 2 Aggregated Stats → 3 Categorical Profile → 4 Pattern Insights (identity exchange) → 5 Structured Summary → 6 Full Collaboration.',
      'Each rung only unlocks once BOTH organisations consent to it — advancing is never unilateral. Either party can revoke back to an earlier rung at any time.',
      'The 3→4 step is gated by a signed mutual NDA — the platform will not exchange identities until the agreement is in place, and it records the consent.',
    ],
    category: 'Concepts',
  },
  {
    key: 'concept-pipeline',
    title: 'The innovation pipeline',
    body: 'Every invention moves through the same seven stages: Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure.',
    detail: [
      'Each stage unlocks the next, so you always know what is done and what is coming. The Workflow Progress strip on a project tracks exactly where that project sits.',
    ],
    category: 'Concepts',
  },
  {
    key: 'concept-blind-matching',
    title: 'Blind matching',
    body: 'We find relevant counterparties by comparing embeddings, not by exposing your invention. You control what is revealed and when.',
    category: 'Concepts',
  },
  {
    key: 'concept-byok',
    title: 'BYOK & key custody',
    body: 'Bring Your Own Key lets your organisation hold the encryption key. Platform-managed keys are simpler; BYOK gives you sole custody.',
    category: 'Concepts',
  },
  {
    key: 'concept-fgac',
    title: 'Access roles (FGAC)',
    body: 'Fine-grained access control maps each person to exactly the projects, folders and disclosure levels they are authorised for.',
    category: 'Concepts',
  },
  {
    key: 'support',
    title: 'Support',
    body: 'Raise a support request and follow its thread here. Crash reports can pre-fill a request, and notifications deep-link you back to the ticket.',
    detail: [
      'My Support Tickets lists everything you have raised with its current status. Open a ticket to read the full thread and reply.',
      'New Request starts a blank request; if the app captured a crash, “Report a problem” arrives with the technical details already attached.',
    ],
    category: 'Account',
  },
  {
    key: 'admin-support',
    title: 'Support queue',
    body: 'The platform-wide support queue. Triage, reply to and resolve user tickets — InnovateFlow administrators only.',
    detail: [
      'Open a ticket for the full thread and triage controls. Quarantine-release actions are additionally restricted to security administrators.',
    ],
    category: 'Account',
  },
]

const BY_KEY: Record<string, HelpArticle> = Object.fromEntries(HELP_ARTICLES.map((a) => [a.key, a]))

export function helpFor(key: string | undefined): HelpArticle | undefined {
  return key ? BY_KEY[key] : undefined
}

export interface GlossaryTerm { term: string; definition: string }
export const GLOSSARY: GlossaryTerm[] = [
  { term: 'Disclosure', definition: 'Your formal, timestamped record that you invented something — the start of the pipeline.' },
  { term: 'Disclosure ladder', definition: 'L1–L6 staged reveal of sensitive detail; L3→L4 (identity exchange) needs a signed NDA; L6 is full collaboration.' },
  { term: 'Blind matching', definition: 'Finding counterparties via embeddings without exposing your invention.' },
  { term: 'BYOK', definition: 'Bring Your Own Key — your organisation holds the encryption key.' },
  { term: 'FGAC', definition: 'Fine-grained access control — per-person access to projects, folders and levels.' },
  { term: 'Intake', definition: 'The automated first pass that classifies and prepares a new document.' },
  { term: 'Aggregator', definition: 'An organisation that manages a portfolio of innovations across others.' },
  { term: 'Band', definition: 'The distance-ladder word for how close a record sits to a project’s centre — Very close, Close, Moderately close, Further out, Far afield. The one word for this idea everywhere in the app.' },
  { term: 'Footprint', definition: 'The boundary line the Portfolio Atlas draws around one project’s own documents — where that project’s ground actually is.' },
  { term: 'Overlap', definition: 'Two or more projects working the same ground — their documents sit in intersecting territory. The opposite question to Synergy.' },
  { term: 'Synergy', definition: 'A complementary pairing between two projects — combining them creates something neither has alone. The opposite question to Overlap.' },
  { term: 'Overlap group', definition: 'A cluster of two or more projects an Overlap scan judged to be working the same ground, with its own verdict, evidence and lifecycle.' },
  { term: 'Strongest pair', definition: 'An overlap group’s headline number — its highest-scoring document pair, expressed as a share of the smaller project’s documents matched, not a similarity value.' },
  { term: 'Weak signal', definition: 'An overlap pair scoring 25–39 — shown only when you choose to look, and never forms a group or sends a notification.' },
  // design §5.1 "one metric gets one name" — these glossary headwords must
  // match the card's own plain-English sub-score labels, never the raw
  // internal metric names they replaced.
  { term: 'Openness', definition: 'How little has been filed or published at this exact distance from your project — the biggest input into the White-Space score.' },
  { term: 'Nearby activity', definition: 'Whether there is enough related patent activity nearby to make a region buildable, without so much that it is already staked out.' },
  { term: 'Filing momentum', definition: 'How recently patents have been filed in a band — a recency signal, not a raw count.' },
  { term: 'Feasibility signal', definition: 'How much of a band’s published research a project’s own documents already cover.' },
  { term: 'Combination white space', definition: 'A Combination Matrix cell where a feature and a domain both exist elsewhere, but nobody has paired them — genuinely unexplored, not just under-filed.' },
]

export interface Shortcut { keys: string; action: string }
export const SHORTCUTS: Shortcut[] = [
  { keys: '/', action: 'Focus global search' },
  { keys: 'g then h', action: 'Go to Home' },
  { keys: 'g then p', action: 'Go to Projects' },
  { keys: '?', action: 'Open Help & Resources' },
  { keys: 'Esc', action: 'Close dialog or panel' },
]
